import React, { useState, useEffect } from 'react';
import { userApi, examApi } from '../services/api';
import { Button, Card, EmptyState, ErrorState, LoadingState, PageHeader } from '../components/ui';
import { FiAward, FiBarChart2, FiCheckCircle, FiFileText, FiTarget } from 'react-icons/fi';
import './ExamHistoryPage.css';
import { formatDateTime as formatDate } from '../utils/format';

interface ExamHistoryPageProps {
  onViewResults?: (sessionId: string) => void;
  onStartExam?: () => void;
}

interface ExamHistory {
  sessionId: string;
  examType: string;
  score: number;
  percentage: number;
  timeTakenMinutes: number;
  completedAt: string;
  passed: boolean;
  totalQuestions: number;
  correctAnswers: number;
}

const ExamHistoryPage: React.FC<ExamHistoryPageProps> = ({ onViewResults, onStartExam }) => {
  const [history, setHistory] = useState<ExamHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [examTypes, setExamTypes] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    limit: 5,
    offset: 0,
    total: 0,
    hasMore: false
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadHistory = async (offset: number = 0, append: boolean = false) => {
    try {
      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const response = await userApi.getHistory({ limit: pagination.limit, offset });
      if (response.success && response.data) {
        const responseData = response.data as any;
        const historyData = responseData?.history || [];
        const paginationData = responseData?.pagination || {};
        
        if (append) {
          setHistory(prev => [...prev, ...historyData]);
        } else {
          setHistory(Array.isArray(historyData) ? historyData : []);
        }
        
        setPagination({
          limit: paginationData.limit || pagination.limit,
          offset: paginationData.offset || offset,
          total: paginationData.total || 0,
          hasMore: paginationData.hasMore || false
        });
      } else {
        if (!append) {
          setHistory([]);
          setError('시험 기록을 불러올 수 없습니다.');
        }
      }
    } catch (err) {
      console.error('History loading error:', err);
      if (!append) {
        setHistory([]);
        setError('시험 기록을 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const loadMoreHistory = () => {
    if (pagination.hasMore && !isLoadingMore) {
      const nextOffset = pagination.offset + pagination.limit;
      loadHistory(nextOffset, true);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load exam types first
        const examTypesResponse = await examApi.getAllTypes();
        if (examTypesResponse.success && examTypesResponse.data) {
          setExamTypes(examTypesResponse.data as any[]);
        }
        
        // Load initial history
        await loadHistory(0, false);
      } catch (err) {
        console.error('Data loading error:', err);
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}시간 ${mins}분`;
    }
    return `${mins}분`;
  };

  const getExamTypeInfo = (examTypeId: string) => {
    return examTypes.find(type => type.exam_type_id === examTypeId);
  };

  const getExamTypeName = (examTypeId: string) => {
    const examType = getExamTypeInfo(examTypeId);
    return examType ? examType.name : examTypeId;
  };

  const handleViewDetails = (sessionId: string) => {
    onViewResults?.(sessionId);
  };

  if (isLoading) {
    return (
      <LoadingState label="시험 기록을 불러오는 중..." />
    );
  }

  if (error) {
    return (
      <ErrorState title="시험 기록을 불러오지 못했습니다" description={error} />
    );
  }

  return (
    <div className="exam-history-page">
      <PageHeader title="시험 결과" description="지금까지 응시한 시험 결과를 확인하고 약한 영역을 파악하세요." />

      {history.length === 0 ? (
        <div className="exam-history-page__empty">
          <Card padding="none" tone="major">
            <EmptyState
              icon={<FiBarChart2 />}
              title="아직 완료한 시험이 없습니다"
              description="모의시험을 한 번 완료하면 점수와 영역별 분석이 여기에 쌓입니다."
              action={onStartExam && <Button variant="primary" onClick={onStartExam}>모의시험 시작하기</Button>}
            />
          </Card>
        </div>
      ) : (
        <div className="exam-history-page__content">
          <div className="exam-history-page__stats">
            <div className="history-stats-grid">
              <Card padding="lg" shadow="sm" hover>
                <div className="stat-card">
                  <div className="stat-icon"><FiFileText aria-hidden="true" /></div>
                  <div className="stat-content">
                    <div className="history-stat-number">{pagination.total}</div>
                    <div className="history-stat-label">총 시험 횟수</div>
                  </div>
                </div>
              </Card>
              
              <Card padding="lg" shadow="sm" hover>
                <div className="stat-card">
                  <div className="stat-icon"><FiTarget aria-hidden="true" /></div>
                  <div className="stat-content">
                    <div className="history-stat-number">
                      {history.length > 0 
                        ? Math.round(history.reduce((sum, exam) => sum + exam.percentage, 0) / history.length)
                        : 0
                      }%
                    </div>
                    <div className="history-stat-label">평균 점수</div>
                  </div>
                </div>
              </Card>
              
              <Card padding="lg" shadow="sm" hover>
                <div className="stat-card">
                  <div className="stat-icon"><FiAward aria-hidden="true" /></div>
                  <div className="stat-content">
                    <div className="history-stat-number">
                      {history.length > 0 
                        ? Math.max(...history.map(exam => exam.percentage))
                        : 0
                      }%
                    </div>
                    <div className="history-stat-label">최고 점수</div>
                  </div>
                </div>
              </Card>
              
              <Card padding="lg" shadow="sm" hover>
                <div className="stat-card">
                  <div className="stat-icon"><FiCheckCircle aria-hidden="true" /></div>
                  <div className="stat-content">
                    <div className="history-stat-number">
                      {history.length > 0 
                        ? history.filter(exam => exam.passed).length
                        : 0
                      }
                    </div>
                    <div className="history-stat-label">합격 횟수</div>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          <div className="exam-history-page__list">
            <Card
              title="시험 기록 목록"
              subtitle={`총 ${pagination.total}개의 시험 기록 중 ${history.length}개 표시`}
              padding="none"
              shadow="md"
            >
              <div className="history-table-wrap">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th className="col-exam">시험</th>
                      <th className="col-code">코드</th>
                      <th className="col-date">응시일</th>
                      <th className="col-time">소요 시간</th>
                      <th className="col-correct">정답</th>
                      <th className="col-score">점수</th>
                      <th className="col-status">상태</th>
                      <th className="col-action"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((exam) => (
                      <tr key={exam.sessionId}>
                        <td className="col-exam">
                          <div className="history-table__exam">
                            {getExamTypeInfo(exam.examType)?.icon && (
                              <img src={getExamTypeInfo(exam.examType)!.icon} alt="" className="history-table__icon" />
                            )}
                            <span>{getExamTypeName(exam.examType)}</span>
                          </div>
                        </td>
                        <td className="col-code">
                          <code className="history-table__code">{exam.sessionId.slice(-8)}</code>
                        </td>
                        <td className="col-date">{formatDate(exam.completedAt)}</td>
                        <td className="col-time">{formatTime(exam.timeTakenMinutes)}</td>
                        <td className="col-correct">{exam.correctAnswers}/{exam.totalQuestions}</td>
                        <td className="col-score">
                          <strong className={`history-table__score ${exam.passed ? 'passed' : 'failed'}`}>
                            {exam.percentage}%
                          </strong>
                        </td>
                        <td className="col-status">
                          <span className={`history-status-badge ${exam.passed ? 'passed' : 'failed'}`}>
                            {exam.passed ? '합격' : '불합격'}
                          </span>
                        </td>
                        <td className="history-table__action col-action">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleViewDetails(exam.sessionId)}
                          >
                            상세
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {pagination.hasMore && (
                <div className="history-load-more">
                  <Button 
                    variant="secondary"
                    size="lg"
                    onClick={loadMoreHistory}
                    disabled={isLoadingMore}
                    style={{ width: '100%', margin: '20px' }}
                  >
                    {isLoadingMore ? (
                      <>
                        <div className="spinner" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                        더 많은 기록 불러오는 중...
                      </>
                    ) : (
                      `더 많은 기록 보기 (${pagination.total - history.length}개 남음)`
                    )}
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamHistoryPage;
